import React, { useEffect, useState } from 'react';
import { adminApi } from '../../api';
import { useApi } from '../../hooks/useApi';
import { Spinner } from '../../components/StateHelpers';
import { useToast } from '../../hooks/use-toast';
import { Save, Send } from 'lucide-react';

const SETTINGS_TABS = [
  { id: 'general', label: 'Genel Ayarlar' },
  { id: 'payment', label: 'Ödeme Yöntemleri' },
  { id: 'social', label: 'Sosyal Medya' },
  { id: 'analytics', label: 'Analitik & SEO' },
  { id: 'theme', label: 'Görünüm' },
  { id: 'smtp', label: 'SMTP (E-Posta)' }
];

const SETTINGS_GROUPS = {
  general: ['site_title', 'site_description', 'show_testimonials', 'show_references', 'contact_email', 'contact_phone', 'contact_location', 'footer_about', 'footer_copyright', 'maintenance_mode'],
  payment: ['bank_iban', 'bank_holder', 'payment_bank_active', 'payment_crypto_active', 'payment_wallet_active', 'payment_paytr_active', 'paytr_merchant_id', 'paytr_merchant_key', 'paytr_merchant_salt', 'paytr_test_mode'],
  social: ['social_discord', 'social_instagram', 'social_youtube', 'social_facebook', 'social_twitter', 'social_whatsapp', 'social_whatsapp_channel', 'social_telegram'],
  analytics: ['google_analytics_id', 'google_tag_manager_id', 'meta_robots_default', 'social_og_image'],
  theme: ['site_logo', 'site_favicon', 'custom_css', 'custom_js'],
  smtp: ['smtp_host', 'smtp_port', 'smtp_user', 'smtp_pass', 'smtp_protocol', 'smtp_from_name', 'smtp_from_email']
};

const SETTING_LABELS = {
  site_title: 'Site Başlığı', site_description: 'Site Açıklaması',
  show_testimonials: 'Müşteri Yorumları Görünsün Mü (1 = Açık, 0 = Kapalı)',
  show_references: 'Referanslarımız Görünsün Mü (1 = Açık, 0 = Kapalı)',
  contact_email: 'İletişim E-posta',
  contact_phone: 'İletişim Telefonu', contact_location: 'İletişim Adresi / Konum',
  footer_about: 'Footer Hakkımızda Metni', footer_copyright: 'Telif Hakkı Metni', maintenance_mode: 'Bakım Modu (1 = Açık)',
  bank_iban: 'Banka IBAN', bank_holder: 'Banka Hesap Sahibi', payment_bank_active: 'Havale/EFT Aktif (1/0)',
  payment_crypto_active: 'Kripto Ödeme Aktif (1/0)', payment_wallet_active: 'Cüzdan Ödeme Aktif (1/0)',
  payment_paytr_active: 'PayTR Aktif (1/0)', paytr_merchant_id: 'PayTR Mağaza No', paytr_merchant_key: 'PayTR Mağaza Anahtarı',
  paytr_merchant_salt: 'PayTR Mağaza Gizli Anahtarı', paytr_test_mode: 'PayTR Test Modu (1/0)',
  social_discord: 'Discord Linki', social_instagram: 'Instagram Linki', social_youtube: 'YouTube Linki',
  social_facebook: 'Facebook Linki', social_twitter: 'X (Twitter) Linki', social_whatsapp: 'WhatsApp Direkt Linki',
  social_whatsapp_channel: 'WhatsApp Kanalı Linki', social_telegram: 'Telegram Linki',
  google_analytics_id: 'Google Analytics ID', google_tag_manager_id: 'Google Tag Manager ID', meta_robots_default: 'Varsayılan Meta Robots', social_og_image: 'Sosyal Medya Paylaşım Görseli (OG Image)',
  site_logo: 'Site Logo (URL/İsim)', site_favicon: 'Site Favicon (URL/İsim)', custom_css: 'Özel CSS (Etiketsiz)', custom_js: 'Özel JS (Etiketsiz)',
  smtp_host: 'SMTP Sunucusu', smtp_port: 'SMTP Portu', smtp_user: 'SMTP Kullanıcı Adı', smtp_pass: 'SMTP Şifre',
  smtp_protocol: 'SMTP Protokolü (ssl/tls)', smtp_from_name: 'Gönderen Adı', smtp_from_email: 'Gönderen E-Posta'
};

export default function AdminSettings() {
  const { data, loading } = useApi(() => adminApi.list('settings'), []);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState('');
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    if (data) {
      const map = {};
      data.forEach((s) => { map[s.setting_key] = s.setting_value; });
      setValues(map);
    }
  }, [data]);

  const set = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.saveSettings(values);
      toast({ title: 'Ayarlar kaydedildi ✓' });
      setTimeout(() => { window.location.reload(); }, 600);
    } catch { toast({ title: 'Hata', description: 'Ayarlar kaydedilemedi', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const testMail = async () => {
    if (!testTo) return;
    try {
      const r = await adminApi.sendMail({ to: testTo, subject: 'Aegis SMTP Test', html: '<h2>SMTP Çalışıyor ✓</h2><p>Bu bir test e-postasıdır.</p>' });
      toast({ title: r.ok ? 'Test maili gönderildi ✓' : 'Mail gönderilemedi (SMTP ayarlarınızı kontrol edin)', variant: r.ok ? undefined : 'destructive' });
    } catch (e) { toast({ title: 'Hata', description: e.message || 'Bağlantı zaman aşımına uğradı veya Nginx tarafından kesildi.', variant: 'destructive' }); }
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1"><h1 className="font-display text-3xl font-black">Ayarlar</h1><p className="text-white/50 text-sm">Site geneli konfigürasyon.</p></div>
        <button onClick={save} disabled={saving} className="btn-gold h-10 px-5 rounded-lg inline-flex items-center gap-2 text-xs font-extrabold tracking-widest disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? 'KAYDEDİLİYOR' : 'KAYDET'}
        </button>
      </div>

      <div className="flex flex-wrap gap-4 mb-6 border-b border-white/10 pb-2">
        {SETTINGS_TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`text-sm font-bold pb-2 ${activeTab === tab.id ? 'text-[#e4b44e] border-b-2 border-[#e4b44e]' : 'text-white/50 hover:text-white'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {(SETTINGS_GROUPS[activeTab] || []).map((k) => {
          const isToggle = [
            'show_references', 'show_testimonials', 'maintenance_mode',
            'payment_bank_active', 'payment_crypto_active', 'payment_wallet_active',
            'payment_paytr_active', 'paytr_test_mode', 'shopier_is_active'
          ].includes(k);
          const isTrue = values[k] === '1' || values[k] === 1 || values[k] === true || values[k] === 'true';

          return (
            <div key={k} className="rounded-xl border border-white/8 bg-[#0d0d13] p-4 flex flex-col justify-between">
              <label className="text-xs tracking-widest text-[#e4b44e]/85 uppercase font-bold">{SETTING_LABELS[k] || k.replace(/_/g, ' ')}</label>
              {isToggle ? (
                <button
                  type="button"
                  onClick={() => set(k, isTrue ? '0' : '1')}
                  className={`mt-3 flex items-center justify-between w-full h-11 px-4 rounded-xl border transition-all ${
                    isTrue
                      ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300 shadow-[0_0_15px_rgba(16,185,129,0.15)]'
                      : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10'
                  }`}
                >
                  <span className="text-xs font-extrabold tracking-wider uppercase">
                    {isTrue ? '✓ AÇIK (SİTEDE GÖRÜNÜR)' : '✖ KAPALI (GİZLİ)'}
                  </span>
                  <div className={`w-11 h-6 rounded-full p-0.5 transition-colors ${isTrue ? 'bg-emerald-500' : 'bg-white/20'}`}>
                    <div className={`w-5 h-5 rounded-full bg-white shadow-md transition-transform ${isTrue ? 'translate-x-5' : 'translate-x-0'}`} />
                  </div>
                </button>
              ) : (k.includes('about') || k.includes('description') || k.includes('custom_')) ? (
                <textarea rows={4} value={values[k] || ''} onChange={(e) => set(k, e.target.value)} className="mt-2 w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:border-[#e4b44e] outline-none transition" />
              ) : (
                <input type={k.includes('pass') || k.includes('key') ? 'password' : 'text'} value={values[k] || ''} onChange={(e) => set(k, e.target.value)} className="mt-2 w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:border-[#e4b44e] outline-none transition" />
              )}
            </div>
          );
        })}
      </div>

      {activeTab === 'smtp' && (
        <div className="mt-10 rounded-2xl border border-[#e4b44e]/30 bg-[#e4b44e]/5 p-6">
          <h3 className="font-display text-xl font-bold text-white mb-2">SMTP Test</h3>
          <p className="text-white/60 text-sm mb-4">SMTP ayarlarınızı yukarıdan kaydedip test maili gönderebilirsiniz.</p>
          <div className="flex gap-2">
            <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="Test e-postası alacak adres" className="flex-1 h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
            <button onClick={testMail} className="btn-outline-gold h-10 px-5 rounded-lg inline-flex items-center gap-2 text-xs font-bold tracking-widest"><Send className="w-4 h-4" /> TEST GÖNDER</button>
          </div>
        </div>
      )}
    </div>
  );
}
